import type {
  BlockSwitchIssue,
  BlockSwitchIssueMap,
  SwitchValidationRow,
  SwitchValidationResult
} from '@/components/building-block-form/utils/switchValidation'
import { ISSUE_SEVERITIES, type IssueSeverity } from '@/constants/issueSeverity'

type CustomBlockSignature = {
  id: number
  name: string
  unit: string
}

type CustomMaterialSignature = {
  id: number
  block: number
  product: number
  lifetime: number
  impact_unit: string
}

type CustomActivitySignature = {
  id: number
  material: number
  stage: string | null
  mode: string
  value: number | null
  equipment_factor?: number
}

export const normalizeDatabaseId = (value: number | string | null | undefined): number | null => {
  if (value == null) return null
  const parsedValue = Number(value)
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) return null
  return parsedValue
}

export const toBlockSwitchIssueMap = (
  validationResult: SwitchValidationResult
): BlockSwitchIssueMap => {
  const blockMap: BlockSwitchIssueMap = {}
  const baseDatabaseName = validationResult?.base_database?.name

  for (const missingRow of validationResult?.missing_rows ?? []) {
    const blockId = Number(missingRow.block_id)
    if (!blockMap[blockId]) {
      blockMap[blockId] = {
        severity: ISSUE_SEVERITIES.MISSING,
        baseDatabaseName,
        missingRows: [],
        changedRows: []
      }
    }
    blockMap[blockId].missingRows.push(missingRow)
    blockMap[blockId].severity = ISSUE_SEVERITIES.MISSING
  }

  for (const changedRow of validationResult?.changed_rows ?? []) {
    const blockId = Number(changedRow.block_id)
    if (!blockMap[blockId]) {
      blockMap[blockId] = {
        severity: ISSUE_SEVERITIES.CHANGED,
        baseDatabaseName,
        missingRows: [],
        changedRows: []
      }
    }
    blockMap[blockId].changedRows.push(changedRow)
  }

  return blockMap
}

export const hasChangedSwitchIssues = (issueMap: BlockSwitchIssueMap): boolean => {
  return Object.values(issueMap).some((issue) => issue.changedRows.length > 0)
}

const rowStillExists = (
  row: SwitchValidationRow,
  existingBlockIds: Set<number>,
  existingMaterialIds: Set<number>
) => {
  return (
    existingBlockIds.has(Number(row.block_id)) && existingMaterialIds.has(Number(row.material_id))
  )
}

const resolveIssueSeverity = (
  issue: Pick<BlockSwitchIssue, 'missingRows' | 'changedRows'>
): IssueSeverity => {
  return issue.missingRows.length > 0 ? ISSUE_SEVERITIES.MISSING : ISSUE_SEVERITIES.CHANGED
}

export const pruneResolvedSwitchIssues = (
  issueMap: BlockSwitchIssueMap,
  existingBlockIds: Set<number>,
  existingMaterialIds: Set<number>
): BlockSwitchIssueMap => {
  const nextIssueMap: BlockSwitchIssueMap = {}

  for (const [rawBlockId, issue] of Object.entries(issueMap)) {
    const blockId = Number(rawBlockId)
    if (!existingBlockIds.has(blockId)) continue

    const missingRows = issue.missingRows.filter((row) =>
      rowStillExists(row, existingBlockIds, existingMaterialIds)
    )
    const changedRows = issue.changedRows.filter((row) =>
      rowStillExists(row, existingBlockIds, existingMaterialIds)
    )

    if (!missingRows.length && !changedRows.length) continue

    nextIssueMap[blockId] = {
      severity: resolveIssueSeverity({ missingRows, changedRows }),
      baseDatabaseName: issue.baseDatabaseName,
      missingRows,
      changedRows
    }
  }

  return nextIssueMap
}

export const clearChangedSwitchIssues = (issueMap: BlockSwitchIssueMap): BlockSwitchIssueMap => {
  const nextIssueMap: BlockSwitchIssueMap = {}

  for (const [rawBlockId, issue] of Object.entries(issueMap)) {
    const blockId = Number(rawBlockId)
    if (!issue.missingRows.length) continue

    nextIssueMap[blockId] = {
      severity: ISSUE_SEVERITIES.MISSING,
      baseDatabaseName: issue.baseDatabaseName,
      missingRows: [...issue.missingRows],
      changedRows: []
    }
  }

  return nextIssueMap
}

export const buildCustomDataSignature = (payload: {
  blocks: CustomBlockSignature[]
  materials: CustomMaterialSignature[]
  activities: CustomActivitySignature[]
}): string => {
  const blocks = [...payload.blocks].sort((a, b) => a.id - b.id)
  const materials = [...payload.materials].sort((a, b) => a.id - b.id)
  const activities = [...payload.activities].sort((a, b) => a.id - b.id)

  return JSON.stringify({
    blocks,
    materials,
    activities
  })
}

export const extractDatabaseNameFromFileName = (fileName: string): string => {
  const trimmed = fileName.trim()
  if (!trimmed) return 'database'
  const stem = trimmed.replace(/\.[^/.]+$/, '').trim()
  return stem || 'database'
}

export const resolveDatabaseNameById = (
  databases: Array<{ id: number; name: string }>,
  databaseId: number | null,
  fallback = 'selected database'
): string => {
  if (databaseId === null) return fallback
  const matchedDatabase = databases.find((database) => Number(database.id) === Number(databaseId))
  return matchedDatabase?.name || fallback
}
